'use client';

import { SectionBasic } from '@sbercloud/uikit-product-site-section';
import { TariffsCarousel } from 'src/components/Carousel/Carousels';
import { WebinarsCarousel } from 'src/components/Carousel/Carousels/WebinarCarousel';
import { PageBlocks } from 'src/components/PageBlocks';
import { type Anchor } from 'src/components/ProductDetailed/AnchorMenu';
import { useIntersectionObserveForms } from 'src/hooks/useIntersectionObserverForms';
import { useLayoutType } from 'src/hooks/useLayoutType';
import { Layout } from 'src/layout';
import {
  type EventCardType,
  type ProductProps,
} from 'src/types/backendContent';
import { type Breadcrumb } from 'src/uikit/Breadcrumbs';
import { HeroInnerPage, HeroInnerPageProps } from 'src/uikit/HeroInnerPage';
import { scrollToElementWithId } from 'src/utils/scrollToElementWithId';

import { RegistryFormBlock } from './helperComponents';
import s from './Solution.module.scss';

const PRICING_DATA = {
  title: 'Стоимость инфраструктуры для 1С',
  slides: [
    {
      title: '1 - 100 пользователей',
      price: 'от 23 029 ₽ в месяц с НДС',
      features: [
        { key: 'СУБД', value: '8 vCPU 16 ГБ RAM 500 ГБ SSD' },
        { key: '1C', value: '2 vCPU 8 ГБ RAM 120 ГБ SSD' },
        { key: 'Веб-сервер', value: '1 vCPU 4 ГБ RAM 80 ГБ SSD' },
      ],
    },
    {
      title: '100 - 500 пользователей',
      price: 'от 48 050 ₽ в месяц с НДС',
      features: [
        { key: 'СУБД', value: '12 vCPU 64 ГБ RAM 1000 ГБ SSD' },
        { key: '1C', value: '4 vCPU 24 ГБ RAM 200 ГБ SSD' },
        { key: 'Веб-сервер', value: '1 vCPU 4 ГБ RAM 80 ГБ SSD' },
      ],
    },
    {
      title: 'от 500 пользователей',
      price: 'от 158 318 ₽ в месяц с НДС',
      features: [
        { key: 'СУБД', value: '40 vCPU 512 ГБ RAM 3000 ГБ SSD' },
        { key: '1C', value: '10 vCPU 64 ГБ RAM 500 ГБ SSD' },
        { key: 'Веб-сервер', value: '2 vCPU 8 ГБ RAM 120 ГБ SSD' },
      ],
    },
  ],
};

const PARTNERS_DATA = {
  title: 'Наши партнеры',
  note: 'Эти компании помогут начать работать в облаке: построят инфраструктуру, подключат сервисы,<br/> перенесут приложения, рассчитают стоимость и возьмут на себя поддержку.',
  images: [
    'https://cdn.cloud.ru/backend/images/solutions/retail-new/partners_img_1.svg',
    'https://cdn.cloud.ru/backend/images/solutions/retail-new/partners_img_2.svg',
    'https://cdn.cloud.ru/backend/images/solutions/retail-new/partners_img_3.svg',
    'https://cdn.cloud.ru/backend/images/solutions/retail-new/partners_img_4.svg',
    'https://cdn.cloud.ru/backend/images/solutions/retail-new/partners_img_5.svg',
  ],
};

const SELECT_OPTIONS = [
  {
    value: 'value1',
    label: 'Обеспечить стабильность и отказоустойчивость',
  },
  { value: 'value2', label: 'Улучшить производительность 1С-систем' },
  { value: 'value3', label: 'Предоставить гибкость ресурсов' },
  { value: 'value4', label: 'Разместить бэкап 1С в облаке' },
  { value: 'value5', label: 'Оптимизировать рабочие процессы' },
  { value: 'value6', label: 'Отказаться от аренды и покупки «железа»' },
];

interface PartnersBlockProps {
  title: string;
  note?: string;
  images: string[];
}

function PartnersBlock({ title, note, images }: PartnersBlockProps) {
  const layoutType = useLayoutType();

  const imagesList = images.map((img, index) => {
    return (
      <div
        key={`${index}_${img}`}
        className={s.partnersBlockImageWrapper}
        data-layout-type={layoutType}
      >
        <img src={img} width={140} height={30} alt="partner-img" />
      </div>
    );
  });

  return (
    <SectionBasic
      id="partners"
      layoutType={layoutType}
      title={title}
      description={note}
    >
      <div className={s.partnersBlockGrid} data-layout-type={layoutType}>
        {imagesList}
      </div>
    </SectionBasic>
  );
}

export function SolutionPage({
  solution,
  anchors,
  webinars,
  breadcrumbs,
  tags,
}: {
  solution: ProductProps;
  anchors: Anchor[];
  webinars: EventCardType[];
  breadcrumbs: Breadcrumb[];
  tags?: HeroInnerPageProps['tags'];
}) {
  const { formRef, setAnchorId } = useIntersectionObserveForms({
    formName: 'lead-solutions',
  });

  const onHeroButtonClickAction = () => {
    scrollToElementWithId('form');
    setAnchorId('solution_anchor_to_form');
  };

  const multipleComment =
    solution.slug === 'vse-programmy-1s-na-serverah-cloud'
      ? {
          options: SELECT_OPTIONS,
          name: 'comment',
          label: 'Задача для размещения 1С в облаке',
        }
      : undefined;

  return (
    <Layout>
      <div className={s.root}>
        <HeroInnerPage
          title={solution.title}
          description={solution.description}
          heroImage={solution.icon3d}
          anchorMenu={{ anchors: anchors, slug: solution.slug }}
          buttons={[
            {
              title: solution.buttonTitle || 'Связаться с нами',
              analyticsOptions: {
                action: 'click',
                clickZone: 'body',
                clickContent: 'Связаться с нами',
                clickElement: 'button',
                uniqueId: `hero-button-scroll-to-form-${solution.title}`,
                transitionType: 'inside-link',
                clickZoneCategory: 'site-body',
              },
              onClick: onHeroButtonClickAction,
            },
          ]}
          breadcrumbs={{
            links: breadcrumbs,
          }}
          tags={tags}
        />

        <PageBlocks blocks={solution.content.blocks} />

        {solution.slug === 'vse-programmy-1s-na-serverah-cloud' && (
          <TariffsCarousel
            id="tariffs"
            title={PRICING_DATA.title}
            slides={PRICING_DATA.slides}
          />
        )}

        {solution.slug === 'retail' && (
          <PartnersBlock
            title={PARTNERS_DATA.title}
            note={PARTNERS_DATA.note}
            images={PARTNERS_DATA.images}
          />
        )}

        {webinars.length > 0 && (
          <WebinarsCarousel title="Вебинары" slides={webinars} />
        )}

        {/* TODO: когда form будет обязательным полем - убрать проверку */}
        {solution.form && (
          <RegistryFormBlock
            {...solution.form}
            formRef={formRef}
            multipleComment={multipleComment}
          />
        )}
      </div>
    </Layout>
  );
}
